// @ts-nocheck
import { createSelector } from "reselect";
import { createReducer } from "libLegacy/internal/store";
import { RootState, AppThunk } from "./state";
import { getCookie, eraseCookie, setCookie } from "libLegacy/internal/cookie";
import { jwt_payload } from "libLegacy/internal/jwt";
import { Action } from "libLegacy/ducks";
import { newNotification, dismissAllNotificationsFrom } from "./notification";
import { ThunkDispatch } from "redux-thunk";
import {
  authenticate,
  LoginResponse,
  LoginForm,
  StatusCredential,
} from "libLegacy/api/auth";

const REQUEST_SIGNIN = "REQUEST_SIGNIN";
const RESPONSE_SIGNIN = "RESPONSE_SIGNIN";
const FAIL_SIGNIN = "FAIL_SIGNIN";
export const UNAUTHORIZED = "UNAUTHORIZED";
const TOKENSWAP = "TOKENSWAP";
const RESPONSE_RECOVERY_PASSWORD = "REQUEST_RECOVERY_PASSWORD";
const CLEAR_RESETID_STATUSCODE = "CLEAR_RESETID_STATUSCODE";

export type JWTPayload = {
  [k: string]: any;
  exp: number;
  userID: string;
  doctID: string;
};

interface AuthState {
  statuscode: boolean;
  verification: string;
  resetID: string;
  token: {
    raw: string;
    payload: JWTPayload;
  };
  statusMonitoringPlatform?: StatusCredential;
  statusEnergyOperator?: StatusCredential;
  currentAcveID?: { acveID: string; email: string };
}

export type InitialState = AuthState;

const COOKIENAME = `token-${process.env.REACT_APP_APPLICATION_ID}`;
const fromCookie = getCookie(COOKIENAME) || "";
const initialState: InitialState = {
  statuscode: false,
  verification: "",
  resetID: "",
  token: {
    raw: fromCookie,
    payload: jwt_payload(fromCookie) || { exp: 0 },
  },
};

// Reducers
export const authReducer = createReducer(initialState, {
  [REQUEST_SIGNIN](state: InitialState, a: Action) {
    return state;
  },
  [RESPONSE_SIGNIN](state: InitialState, a: Action) {
    state.statusMonitoringPlatform = a.response.statusMonitoringPlatform;
    state.statusEnergyOperator = a.response.statusEnergyOperator;
    return state;
  },
  [TOKENSWAP](state: InitialState, a: Action & { token: string }) {
    let token = a.token;
    state.token.raw = token;
    state.token.payload = jwt_payload(token);
    return state;
  },
  [FAIL_SIGNIN](state: InitialState, a: Action) {
    return state;
  },
  [UNAUTHORIZED](state: InitialState, a: Action) {
    state.token.raw = "";
    state.token.payload = { exp: 0, userID: "", doctID: "" };
    return state;
  },
  [RESPONSE_RECOVERY_PASSWORD](state: InitialState, a: any) {
    state.resetID = a.resetID;
    return state;
  },
  [CLEAR_RESETID_STATUSCODE](state: InitialState, a: any) {
    state.statuscode = false;
    state.resetID = "";
    return state;
  },
});

// Selectors
const authSelector = (state: RootState) => state.auth;

export const getToken = createSelector(authSelector, (auth) => ({
  token: auth.token || "",
}));

export const getPayload = createSelector(authSelector, (auth) => ({
  payload: auth.token.payload || {},
}));

export const getCredentialStatus = createSelector(authSelector, (auth) => ({
  statusEnergyOperator: auth.statusEnergyOperator,
  statusMonitoringPlatform: auth.statusMonitoringPlatform,
}));

// Actions
function requestSignin(formData: any) {
  return {
    type: REQUEST_SIGNIN,
    form: formData,
  };
}

function responseSignin(formData: any, json: any) {
  return {
    type: RESPONSE_SIGNIN,
    form: formData,
    response: json,
    receivedAt: Date.now(),
  };
}

export function tokenSwap(formData: any, json: any) {
  let token = json.token ? json.token.replace("Bearer ", "") : "";
  const d = new Date();
  // Always set the cookie to expire 30 days later.
  d.setDate(d.getDate() + 30);
  setCookie(COOKIENAME, token, d);
  return {
    type: TOKENSWAP,
    form: formData,
    token,
    response: json,
    receivedAt: Date.now(),
  };
}

function failSignin(formData: any, json?: any) {
  return {
    type: FAIL_SIGNIN,
    form: formData,
    response: json,
    receivedAt: Date.now(),
  };
}

export function unauthorized() {
  eraseCookie(COOKIENAME);
  return {
    type: UNAUTHORIZED,
    receivedAt: Date.now(),
  };
}

export function login(formData: LoginForm): AppThunk<Promise<LoginResponse>> {
  return async (dispatch: ThunkDispatch<any, any, any>) => {
    dispatch(dismissAllNotificationsFrom("auth"));
    dispatch(requestSignin(formData));

    return authenticate(formData)
      .then((r) => {
        dispatch(responseSignin(formData, r));
        dispatch(tokenSwap(formData, r));
        return Promise.resolve(r);
      })
      .catch((e) => {
        dispatch(
          newNotification("auth", {
            status: "error",
            message: e,
          })
        );
        dispatch(
          failSignin(formData, {
            error: [e],
          })
        );
        return Promise.reject(e);
      });
  };
}
